import { PackagerJobViewModel } from './packager-job.view-model';
import {
  EShakaPackagerJobStatus,
  ShakaPackagerJob,
  ShakaPackagerProcessLogSummary,
} from '../../../../domain/media-transcoder/shaka-packager-job';

export interface PackagerStatusFilterOption {
  value: EShakaPackagerJobStatus | null;
  label: string;
}

export interface IPackagerViewModelApi {
  updateDOM(): void;
  getJobs(): Promise<ShakaPackagerJob[]>;
  getProcessLog(jobUid: string): Promise<Record<string, unknown>>;
}

export class MediaTranscoderPackagerViewModel {
  selectedJob: PackagerJobViewModel | null = null;
  selectedLogJson: string | null = null;
  selectedLogSummary: ShakaPackagerProcessLogSummary | null = null;
  reportError: string | null = null;

  allJobs: PackagerJobViewModel[] = [];
  nameFilter = '';
  selectedStatusFilter: EShakaPackagerJobStatus | null = null;
  readonly statusFilterOptions: PackagerStatusFilterOption[];

  constructor(private _api: IPackagerViewModelApi) {
    this.statusFilterOptions = [
      { value: null, label: 'All' },
      ...Object.keys(EShakaPackagerJobStatus)
        .filter((key) => isNaN(Number(key)))
        .map((key) => ({
          value:
            EShakaPackagerJobStatus[
              key as keyof typeof EShakaPackagerJobStatus
            ],
          label: key,
        })),
    ];
  }

  get jobs(): PackagerJobViewModel[] {
    let result = this.allJobs;

    if (this.selectedStatusFilter !== null) {
      result = result.filter(
        (job) => job.jobStatus === this.selectedStatusFilter,
      );
    }

    const nameQuery = this.nameFilter.trim().toLowerCase();
    if (nameQuery) {
      result = result.filter((job) =>
        job.mediaName.toLowerCase().includes(nameQuery),
      );
    }

    return [...result].sort((a, b) =>
      b.lastJobStatusUpdate.diff(a.lastJobStatusUpdate),
    );
  }

  setNameFilter(name: string): void {
    this.nameFilter = name;
    this._api.updateDOM();
  }

  setStatusFilter(status: EShakaPackagerJobStatus | null): void {
    this.selectedStatusFilter = status;
    this._api.updateDOM();
  }

  async refreshJobs(): Promise<void> {
    const jobs = await this._api.getJobs();
    this.allJobs = jobs.map((job) => new PackagerJobViewModel(job));
    this._api.updateDOM();
  }

  async showReport(job: PackagerJobViewModel): Promise<void> {
    this.selectedJob = job;
    this.selectedLogJson = null;
    this.selectedLogSummary = null;
    this.reportError = null;
    this._api.updateDOM();

    try {
      const log = await this._api.getProcessLog(job.id);
      this.selectedLogSummary = this._buildSummary(log);
      this.selectedLogJson = JSON.stringify(log, null, 2);
    } catch {
      this.reportError = 'Failed to load process log.';
    }

    this._api.updateDOM();
  }

  closeReport(): void {
    this.selectedJob = null;
    this.selectedLogJson = null;
    this.selectedLogSummary = null;
    this.reportError = null;
  }

  private _buildSummary(
    log: Record<string, unknown>,
  ): ShakaPackagerProcessLogSummary {
    const destinationPaths = (log.destinationPaths ??
      log.DestinationPaths) as Record<string, unknown> | undefined;
    const stagesRaw = (log.stages ?? log.Stages) as
      | Array<Record<string, unknown>>
      | undefined;
    const errors = (log.errors ?? log.Errors) as string[] | undefined;
    const warnings = (log.warnings ?? log.Warnings) as string[] | undefined;

    return {
      status: (log.status ?? log.Status) as string | undefined,
      mode: (log.mode ?? log.Mode) as string | undefined,
      videosBucketPath: (destinationPaths?.videosBucketPath ??
        destinationPaths?.VideosBucketPath) as string | undefined,
      metadataBucketPath: (destinationPaths?.metadataBucketPath ??
        destinationPaths?.MetadataBucketPath) as string | undefined,
      errors: errors ?? [],
      warnings: warnings ?? [],
      stages: (stagesRaw ?? []).map((stage) => ({
        name: String(stage.name ?? stage.Name ?? ''),
        durationMs: Number(stage.durationMs ?? stage.DurationMs ?? 0),
        status: String(stage.status ?? stage.Status ?? ''),
        errorMessage: (stage.errorMessage ?? stage.ErrorMessage) as
          | string
          | undefined,
      })),
    };
  }
}
